import type { IOperator } from "@/models/tables";
import i18n from "@/i18n";

export enum ApiOperators {
  DEFAULT = "",
  EQUAL = "eq",
  LIKE = "like",
  NOT_LIKE = "nlike",
  GREATER_THAN = "gt",
  GREATER_THAN_OR_EQUAL_TO = "gte",
  LESS_THAN = "lt",
  LESS_THAN_OR_EQUAL_TO = "lte",
  NOT_EQUAL = "neq",
  BEFORE = "before",
  AFTER = "after",
  ALL = "all"
}

export enum FilterOperators {
  ALL = "all",
  ANY = "any",
  NONE = "none",
  CONTAINS = "*",
  STARTS_WITH = "^",
  ENDS_WITH = "$",
  EQUAL = "=",
  NOT_EQUAL = "neq",
  GREATER_THAN = "gt",
  GREATER_THAN_OR_EQUAL_TO = "gte",
  LESS_THAN = "lt",
  LESS_THAN_OR_EQUAL_TO = "lte",
  AFTER = ">",
  BEFORE = "<",
  AFTER_RELATIVE = "ar",
  BEFORE_RELATIVE = "br",
  ON_OR_BEFORE_DATE = "be",
  ON_OR_AFTER_DATE = "af",
  IS_SET = "set",
  IS_NOT_SET = "nset",
  IS_TRUE = "is_true",
  IS_FALSE = "is_false"
}

export enum FilterTypes {
  CUSTOM = "$custom",
  STRING = "string",
  STRING_EQUAL = "string_equal",
  SELECT = "select",
  SELECT_RADIO = "select-radio",
  SELECT_RADIO_GROUP = "select-radio-group",
  SELECT_CHECKBOX = "select-checkbox",
  SELECT_MULTI = "select-multi",
  TAG = "tag",
  DATE = "date",
  MONTH = "month",
  NUMBER = "number",
  CATEGORY_DATA_SELECTOR = "category-data-selector",
  CUSTOM_NEGATIVE = "custom-negative",
  ASYNC_MULTI = "async-multi"
}

export const FILTER_TYPES_WITH_ARRAY_VALUES = [
  FilterTypes.TAG,
  FilterTypes.SELECT_MULTI,
  FilterTypes.SELECT_CHECKBOX,
  FilterTypes.CATEGORY_DATA_SELECTOR,
  FilterTypes.ASYNC_MULTI
];
export const FILTER_ARRAY_VALUES_SEPARATOR = ",";
export const FRONTEND_FILTER_ARRAY_VALUES_SEPARATOR = "&&";

export const FILTER_OPERATORS: () => IOperator[] = () => [
  {
    key: FilterOperators.EQUAL,
    label: i18n.t("_.is"),
    apiOperator: ApiOperators.DEFAULT,
    types: [
      FilterTypes.STRING,
      FilterTypes.CUSTOM,
      FilterTypes.STRING_EQUAL,
      FilterTypes.DATE,
      FilterTypes.NUMBER,
      FilterTypes.SELECT,
      FilterTypes.SELECT_RADIO,
      FilterTypes.SELECT_RADIO_GROUP,
      FilterTypes.SELECT_CHECKBOX,
      FilterTypes.ASYNC_MULTI
    ]
  },
  {
    key: FilterOperators.NOT_EQUAL,
    label: i18n.t("_.is_not"),
    apiOperator: ApiOperators.NOT_EQUAL,
    types: [
      FilterTypes.STRING,
      FilterTypes.CUSTOM,
      FilterTypes.NUMBER,
      FilterTypes.SELECT,
      FilterTypes.SELECT_RADIO,
      FilterTypes.SELECT_RADIO_GROUP,
      FilterTypes.SELECT_CHECKBOX,
      FilterTypes.CUSTOM_NEGATIVE,
      FilterTypes.ASYNC_MULTI
    ]
  },
  {
    key: FilterOperators.CONTAINS,
    label: i18n.t("_.contains"),
    apiOperator: ApiOperators.LIKE,
    types: [FilterTypes.STRING, FilterTypes.CUSTOM]
  },
  {
    key: FilterOperators.STARTS_WITH,
    label: i18n.t("_.starts_with"),
    apiOperator: ApiOperators.LIKE,
    types: [FilterTypes.STRING, FilterTypes.CUSTOM]
  },
  {
    key: FilterOperators.ENDS_WITH,
    label: i18n.t("_.ends_with"),
    apiOperator: ApiOperators.LIKE,
    types: [FilterTypes.STRING, FilterTypes.CUSTOM]
  },
  {
    key: FilterOperators.GREATER_THAN,
    label: i18n.t("_.is_greater_than"),
    apiOperator: ApiOperators.GREATER_THAN,
    types: [FilterTypes.NUMBER, FilterTypes.CUSTOM]
  },
  {
    key: FilterOperators.GREATER_THAN_OR_EQUAL_TO,
    label: i18n.t("_.is_greater_or_equal"),
    apiOperator: ApiOperators.GREATER_THAN_OR_EQUAL_TO,
    types: [FilterTypes.NUMBER, FilterTypes.CUSTOM]
  },
  {
    key: FilterOperators.LESS_THAN,
    label: i18n.t("_.is_less_than"),
    apiOperator: ApiOperators.LESS_THAN,
    types: [FilterTypes.NUMBER, FilterTypes.CUSTOM]
  },
  {
    key: FilterOperators.LESS_THAN_OR_EQUAL_TO,
    label: i18n.t("_.is_less_than_or_equal_to"),
    apiOperator: ApiOperators.LESS_THAN_OR_EQUAL_TO,
    types: [FilterTypes.NUMBER, FilterTypes.CUSTOM]
  },
  {
    key: FilterOperators.IS_TRUE,
    label: i18n.t("_.is_true"),
    apiOperator: ApiOperators.EQUAL,
    types: [FilterTypes.CUSTOM]
  },
  {
    key: FilterOperators.IS_FALSE,
    label: i18n.t("_.is_false"),
    apiOperator: ApiOperators.EQUAL,
    types: [FilterTypes.CUSTOM]
  },
  {
    key: FilterOperators.IS_SET,
    label: i18n.t("_.is_set"),
    apiOperator: ApiOperators.NOT_EQUAL,
    types: [FilterTypes.TAG, FilterTypes.CUSTOM]
  },
  {
    key: FilterOperators.IS_NOT_SET,
    label: i18n.t("_.is_not_set"),
    apiOperator: ApiOperators.EQUAL,
    types: [FilterTypes.TAG, FilterTypes.CUSTOM]
  },
  {
    key: FilterOperators.ALL,
    label: i18n.t("_.matches_all_of"),
    apiOperator: ApiOperators.ALL,
    types: [FilterTypes.TAG, FilterTypes.CUSTOM, FilterTypes.ASYNC_MULTI]
  },
  {
    key: FilterOperators.ANY,
    label: i18n.t("_.matches_any_of"),
    apiOperator: ApiOperators.DEFAULT,
    types: [
      FilterTypes.TAG,
      FilterTypes.CATEGORY_DATA_SELECTOR,
      FilterTypes.SELECT_MULTI,
      FilterTypes.CUSTOM,
      FilterTypes.ASYNC_MULTI
    ]
  },
  {
    key: FilterOperators.NONE,
    label: i18n.t("_.matches_none_of"),
    apiOperator: ApiOperators.NOT_EQUAL,
    types: [
      FilterTypes.TAG,
      FilterTypes.CATEGORY_DATA_SELECTOR,
      FilterTypes.SELECT_MULTI,
      FilterTypes.CUSTOM,
      FilterTypes.ASYNC_MULTI
    ]
  },
  {
    key: FilterOperators.AFTER,
    label: i18n.t("_.after_date"),
    apiOperator: ApiOperators.GREATER_THAN,
    types: [
      FilterTypes.DATE,
      FilterTypes.SELECT_RADIO_GROUP,
      FilterTypes.CUSTOM
    ]
  },
  {
    key: FilterOperators.AFTER_RELATIVE,
    label: i18n.t("_.after_relative_date"),
    apiOperator: ApiOperators.AFTER,
    types: [
      FilterTypes.DATE,
      FilterTypes.SELECT_RADIO_GROUP,
      FilterTypes.SELECT, // Needed for inbox listing
      FilterTypes.CUSTOM
    ]
  },
  {
    key: FilterOperators.ON_OR_AFTER_DATE,
    label: i18n.t("_.on_or_after_date"),
    apiOperator: ApiOperators.GREATER_THAN_OR_EQUAL_TO,
    types: [
      FilterTypes.DATE,
      FilterTypes.SELECT_RADIO_GROUP,
      FilterTypes.CUSTOM
    ]
  },
  {
    key: FilterOperators.BEFORE,
    label: i18n.t("_.before_date"),
    apiOperator: ApiOperators.LESS_THAN,
    types: [
      FilterTypes.DATE,
      FilterTypes.SELECT_RADIO_GROUP,
      FilterTypes.CUSTOM
    ]
  },
  {
    key: FilterOperators.BEFORE_RELATIVE,
    label: i18n.t("_.before_relative_date"),
    apiOperator: ApiOperators.BEFORE,
    types: [
      FilterTypes.DATE,
      FilterTypes.SELECT_RADIO_GROUP,
      FilterTypes.SELECT, // Needed for inbox listing
      FilterTypes.CUSTOM
    ]
  },
  {
    key: FilterOperators.ON_OR_BEFORE_DATE,
    label: i18n.t("_.on_or_before_date"),
    apiOperator: ApiOperators.LESS_THAN_OR_EQUAL_TO,
    types: [
      FilterTypes.DATE,
      FilterTypes.SELECT_RADIO_GROUP,
      FilterTypes.CUSTOM
    ]
  }
];

export const RELATIVE_DATE_OPERATORS = [
  FilterOperators.AFTER_RELATIVE,
  FilterOperators.BEFORE_RELATIVE
];

export const OPERATORS_ALLOWED_TO_HAVE_SUBFILTERS = [
  FilterOperators.CONTAINS,
  FilterOperators.STARTS_WITH,
  FilterOperators.ENDS_WITH
];

export const RELATIVE_DATE_PERIODS: {
  value: string;
  label: string;
}[] = [
  {
    value: "hours",
    get label() {
      return i18n.t("hours").toString();
    }
  },
  {
    value: "days",
    get label() {
      return i18n.t("days").toString();
    }
  },
  {
    value: "weeks",
    get label() {
      return i18n.t("weeks").toString();
    }
  },
  {
    value: "months",
    get label() {
      return i18n.t("months").toString();
    }
  },
  {
    value: "years",
    get label() {
      return i18n.t("years").toString();
    }
  }
];

type RelativeDateType = {
  value: string;
  label: string;
};

export const RELATIVE_DATE_TIMES: () => RelativeDateType[] = () => [
  {
    value: "-",
    label: i18n.t("_.ago").toString()
  },
  {
    value: "+",
    label: i18n.t("_.from_now").toString()
  }
];

export enum Sorters {
  ASCENDING = "+",
  DESCENDING = "-"
}

export const RELATIVE_TIME_REGEX = /^[+-]?[0-9]+_[a-z]+$/;
export const SUBFILTER_CONCAT_CHAR = ",";
export const CONSTANT_FILTERS_START_CHAR = "$";
export const ROUTE_QUERY_SEPARATOR = ";";
export const ROUTER_OPERATOR_SEPARATOR = "|";

export const CustomFilter = () => ({
  key: "",
  label: i18n.t("_action.advanced_custom_filter"),
  type: FilterTypes.CUSTOM,
  operator: FilterOperators.CONTAINS,
  value: ""
});
